// ProfilePost.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { USER_PROFILE_HOME_CATEGORIES } from '../RemoteConfigKeys.js';

import PostDraftList from '../container/PostDraftList.js';
import FlixListItem from '../container/FlixListItem.js';
import OutboxMessage from '../container/OutboxMessage.js';
import SortButton from '../container/SortButton.js';
import ProfileCardItem from '../container/ProfileCardItem.js';
import ProfileCategories from '../container/ProfileCategories.js';

import Loading from '../component/Loading.jsx';
import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import {
  ProfileListType,
  ProfileListMediaType,
} from '../resource/profileConstants.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import { isClient } from '../resource/getUserAgent.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { Entry as UploadJobType } from '../resource/uploadConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color, textColor } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import CreatorEmptyPostIconSrouce from '../../img/img_empty_nopost_creator.svg';
import UserEmptyPostIconSrouce from '../../img/img_empty_nopost.svg';
import ShortsEmptyIconSource from '../../img/img_nofollower.svg';

export class ProfilePost extends React.PureComponent {
  state = {
    isServer: true,
  };
  profilePostListElement = React.createRef();
  nextTick = null;
  scrollToElementTimeout = null;
  locationState = {};
  updateProfileFeedUnixTimestamp = () => {
    const {
      isMeProfile,
      profileFeedUnixTimestamp,
      updateProfileFeedUnixTimestamp,
    } = this.props;
    if (isMeProfile && !profileFeedUnixTimestamp) {
      return updateProfileFeedUnixTimestamp({
        unixTimestamp: getCurrentUnixTimestamp(),
      });
    }
  };
  fetchPosts = ({ page, shouldSkipLocalCache }) => {
    const {
      userId,
      meId,
      feedNameWithQuery,
      profileFeedUnixTimestamp,
      isMeProfile,
      fetchFeeds,
    } = this.props;
    const usingUserId = meId && isMeProfile ? meId : userId;
    if (!usingUserId) {
      return;
    }
    return fetchFeeds({
      type: feedNameWithQuery,
      page,
      unixTimestamp: profileFeedUnixTimestamp,
      shouldSkipLocalCache,
    });
  };
  renderLoading = () => {
    return (
      <ItemWrapper>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </ItemWrapper>
    );
  };
  renderEmpty = () => {
    const { listType, isMeProfile, t, openUploadMenuModal } = this.props;
    if (ProfileListType.SHORT === listType) {
      return (
        <EmptyWrapper>
          <EmptyIcon src={ShortsEmptyIconSource} />
          {t('it_is_empty_here', {
            ns: TranslationNamespace.GENERAL,
          })}
        </EmptyWrapper>
      );
    }
    if (!isMeProfile) {
      return (
        <EmptyWrapper>
          <EmptyIcon src={UserEmptyPostIconSrouce} />
          {t('empty_post_list', {
            ns: TranslationNamespace.PROFILE,
          })}
        </EmptyWrapper>
      );
    }
    return (
      <EmptyWrapper>
        <EmptyIcon src={CreatorEmptyPostIconSrouce} />
        <EmptyActionButton
          onClick={() => {
            trackMixpanel({
              type: EventTypes.USER_CLICKED,
              payload: {
                viewId: getViewId({
                  pathname: window.location.pathname,
                }),
                buttonId:
                  ProfileListType.STORY === listType
                    ? ButtonId.Profile.ButtonCreateStory
                    : ProfileListType.IMAGE === listType
                    ? ButtonId.Profile.ButtonCreateImage
                    : ProfileListType.FLIX === listType
                    ? ButtonId.Profile.ButtonCreateVideo
                    : ButtonId.Profile.ButtonCreatePost,
              },
            });
            return openUploadMenuModal();
          }}
        >
          {t('create_post_to_earn', {
            ns: TranslationNamespace.PROFILE,
          })}
        </EmptyActionButton>
      </EmptyWrapper>
    );
  };
  getLocationState = () => {
    const { listType, userId } = this.props;
    const key = `${userId}`;
    if (!this.locationState[key]) {
      this.locationState[key] =
        ProfileListType.STORY === listType
          ? {
              listPath: ['outbox', ProfileListType.STORY, userId],
            }
          : {};
    }
    return this.locationState[key];
  };
  renderPost = ({ id, index }) => {
    const {
      feedNameWithQuery,
      listType,
      pinnedItemIds,
      userProfileStoryLayout,
    } = this.props;
    const isPinned = pinnedItemIds.includes(id);
    const onClickOutboxMessage = () => {
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId: ButtonId.Profile.ButtonPostClick,
          messageId: id,
        },
      });
    };
    switch (listType) {
      case ProfileListType.SHORT: {
        return (
          <ProfileCardItem
            messageId={id}
            categoryId={feedNameWithQuery}
            index={index}
            isPinned={isPinned}
            isProfileCategory
            isThin
            listType={listType}
          />
        );
      }
      case ProfileListType.FLIX:
        return (
          <FlixListItemWrapper>
            <FlixListItem
              messageId={id}
              shouldUseLink
              shortVideoOrigin={'profile'}
              shouldIncrementHeading
              shouldTrackProfilePostEvent
              shouldShowAuthor={false}
              isPinned={isPinned}
            />
          </FlixListItemWrapper>
        );
      case ProfileListType.IMAGE:
        return (
          <OutboxMessageWrapper
            to={`/post/${id}${isClient ? location.search : ''}`}
            state={this.getLocationState()}
            onClick={onClickOutboxMessage}
          >
            <OutboxMessage messageId={id} isPinned={isPinned} />
          </OutboxMessageWrapper>
        );
      case ProfileListType.STORY:
      default:
        return userProfileStoryLayout === 'grid' ? (
          <ProfileCardItem
            messageId={id}
            listType={listType}
            isPinned={isPinned}
            locationState={this.getLocationState()}
          />
        ) : (
          <OutboxMessageWrapper
            to={`/story/${id}${isClient ? location.search : ''}`}
            state={this.getLocationState()}
            onClick={onClickOutboxMessage}
          >
            <OutboxMessage messageId={id} isPinned={isPinned} />
          </OutboxMessageWrapper>
        );
    }
  };
  renderCategories = () => {
    const { userId } = this.props;
    return (
      <ProfileCategories
        remoteConfigKey={USER_PROFILE_HOME_CATEGORIES}
        userId={userId}
      />
    );
  };
  renderList = () => {
    const {
      nextPage,
      isFirstPageFetched,
      isNextPageFetching,
      postIds,
      listType,
      userProfileStoryLayout,
    } = this.props;
    const isShorts = ProfileListType.SHORT === listType;
    const isStoryGridLayout =
      userProfileStoryLayout == 'grid' && ProfileListType.STORY === listType;
    return (
      <InfiniteScroller
        pageStart={1}
        useWindow
        threshold={100}
        hasMore={nextPage != null}
        isFetching={isNextPageFetching}
        loadMore={() => this.fetchPosts({ page: nextPage })}
      >
        <ItemList isGrid={isShorts || isStoryGridLayout} isThin={isShorts}>
          <ScrollItem loader={this.renderLoading()}>
            {[ProfileListType.SHORT, ProfileListType.STORY].includes(
              listType
            ) ? (
              <PostDraftList
                uploadJobType={
                  ProfileListType.SHORT === listType
                    ? UploadJobType.CLIP
                    : UploadJobType.STORY
                }
                mediaTypesString="*"
              />
            ) : null}
            {postIds.map((id, index) => (
              <React.Fragment key={id}>
                {this.renderPost({ id, index })}
              </React.Fragment>
            ))}
          </ScrollItem>
          {!isFirstPageFetched && this.renderLoading()}
        </ItemList>
      </InfiniteScroller>
    );
  };
  componentDidMount() {
    this.fetchPosts({ page: 1 });
    this.nextTick = setTimeout(() => {
      this.updateProfileFeedUnixTimestamp();
      this.setState({
        isServer: false,
      });
    }, 0);
    const { setMessageHeightInFeed } = this.props;
    this.resizeObserver = new ResizeObserver(entries => {
      const messageObject = objectifyArrayById({
        array: entries.map(entry => ({
          id: entry.target.getAttribute('data-message_id'),
          messageHeight: entry.target.clientHeight,
        })),
      });
      return setMessageHeightInFeed({
        feedName: 'profile',
        messageObject,
      });
    });
  }
  componentDidUpdate(prevProps) {
    const { meId, feedNameWithQuery, profileFeedUnixTimestamp, isMeProfile } =
      this.props;
    const isMeChanged = meId !== prevProps.meId;
    const isFeedNameWithQueryChanged =
      feedNameWithQuery !== prevProps.feedNameWithQuery;
    const isTimestampChanged =
      prevProps.profileFeedUnixTimestamp !== profileFeedUnixTimestamp;
    const isMeProfileChanged = isMeProfile !== prevProps.isMeProfile;
    if (isMeProfileChanged) {
      this.updateProfileFeedUnixTimestamp();
    }
    if (isMeChanged || isFeedNameWithQueryChanged || isTimestampChanged) {
      this.fetchPosts({
        page: 1,
        shouldSkipLocalCache: isTimestampChanged && !isFeedNameWithQueryChanged,
      });
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
    clearTimeout(this.scrollToElementTimeout);
    this.resizeObserver?.disconnect();
  }
  render() {
    const {
      outboxFeedName,
      feedNameRemoteConfigKey,
      listType,
      isFirstPageFetched,
      isMeProfile,
      postIds,
      uploadPostIds,
    } = this.props;
    const { isServer } = this.state; // Prevent re-hydrate dom shift on client side
    if (ProfileListType.HOME === listType) {
      return (
        <StyledProfileList ref={this.profilePostListElement}>
          {this.renderCategories()}
        </StyledProfileList>
      );
    }
    const shouldShowPostDraftList =
      [ProfileListType.FLIX, ProfileListType.IMAGE].includes(listType) &&
      !!uploadPostIds.length &&
      !isServer &&
      isMeProfile;
    const shouldShowLocalShorts =
      ProfileListType.SHORT === listType && !!uploadPostIds.length;
    const isEmpty =
      isFirstPageFetched &&
      postIds.length === 0 &&
      !shouldShowPostDraftList &&
      !shouldShowLocalShorts;
    return (
      <StyledProfileList ref={this.profilePostListElement}>
        {isFirstPageFetched && postIds.length === 0 ? null : (
          <ConditionsWrapper>
            <SortButton
              remoteConfigKeyName={feedNameRemoteConfigKey}
              baseFeedName={outboxFeedName}
              mixpanelButtonId={ButtonId.Profile.ButtonProfileSorting}
              defaultItemI18nKey={'outbox_default_sorting'}
            />
          </ConditionsWrapper>
        )}
        {shouldShowPostDraftList && (
          <PostDraftListWrapper>
            <PostDraftList
              uploadJobType={UploadJobType.POST}
              mediaTypesString={
                ProfileListType.FLIX === listType
                  ? ProfileListMediaType.VIDEO
                  : ProfileListType.IMAGE === listType
                  ? ProfileListMediaType.IMAGE
                  : `${ProfileListMediaType.VIDEO},${ProfileListMediaType.IMAGE}`
              }
            />
          </PostDraftListWrapper>
        )}
        {isEmpty ? this.renderEmpty() : this.renderList()}
      </StyledProfileList>
    );
  }
}

ProfilePost.propTypes = {
  userId: PropTypes.string,
  meId: PropTypes.string,
  feedNameWithQuery: PropTypes.string,
  outboxFeedName: PropTypes.string,
  feedNameRemoteConfigKey: PropTypes.string,
  listType: PropTypes.string,
  userProfileStoryLayout: PropTypes.string,
  nextPage: PropTypes.number,
  profileFeedUnixTimestamp: PropTypes.number,
  isFirstPageFetched: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isMeProfile: PropTypes.bool,
  pinnedItemIds: PropTypes.array,
  postIds: PropTypes.array,
  uploadPostIds: PropTypes.array,
  t: PropTypes.func.isRequired,
  openUploadMenuModal: PropTypes.func,
  fetchFeeds: PropTypes.func,
  setMessageHeightInFeed: PropTypes.func,
  updateProfileFeedUnixTimestamp: PropTypes.func,
};

ProfilePost.defaultProps = {
  userId: null,
  meId: null,
  feedNameWithQuery: null,
  outboxFeedName: null,
  feedNameRemoteConfigKey: null,
  listType: null,
  nextPage: null,
  profileFeedUnixTimestamp: null,
  userProfileStoryLayout: null,
  isFirstPageFetched: false,
  isNextPageFetching: false,
  isMeProfile: false,
  pinnedItemIds: [],
  postIds: [],
  uploadPostIds: [],
  openUploadMenuModal: () => null,
  fetchFeeds: () => null,
  setMessageHeightInFeed: () => null,
  updateProfileFeedUnixTimestamp: () => null,
};

const StyledProfileList = styled.div`
  background-color: ${color.black};
`;

const ConditionsWrapper = styled.div`
  padding: 20px 12px;
  display: flex;
  justify-content: flex-end;
  > * {
    :first-child {
      margin-left: 0px;
    }
    margin-left: 8px;
  }
  ${media.mobile`
    padding: 8px 16px;
  `};
  :empty {
    padding: 0px;
    margin-bottom: 20px;
    ${media.mobile`
      margin-bottom: 12px;
    `};
  }
`;

const PostDraftListWrapper = styled.div`
  margin-bottom: 1px;
  width: 100%;
`;

const ItemList = styled.div`
  ${({ isGrid, isThin }) => {
    if (isGrid) {
      return css`
        display: grid;
        grid-template-columns: repeat(
          auto-fill,
          minmax(${isThin ? 168 : 200}px, 1fr)
        );
        place-items: center;
        padding: 16px;
        gap: 20px;
        justify-content: center;
        ${media.mobile`
          gap: 8px;
          grid-template-columns: repeat(
            auto-fill,
            minmax(${isThin ? 112 : 140}px, 1fr)
          );
        `};
      `;
    } else {
      return css`
        display: block;
      `;
    }
  }}
`;

const EmptyWrapper = styled.div`
  padding-top: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-size: 14px;
  color: ${textColor.black};
`;

const EmptyIcon = styled.img`
  margin-bottom: 12px;
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const EmptyActionButton = styled.button`
  ${ResetButtonStyle};
  border: 1px solid ${color.yellow};
  border-radius: 4px;
  padding: 0px 12px;
  height: 36px;
  font-size: 14px;
  color: ${color.yellow};
`;

const ItemWrapper = styled.div`
  position: relative;
  padding-top: 100%;
`;

const FlixListItemWrapper = styled.div`
  :first-child {
    margin-top: 12px;
  }
  :last-child {
    margin-bottom: 0px;
    border-bottom: none;
  }
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  margin-bottom: 8px;
  ${media.mobile`
    padding-left: 16px;
    padding-right: 16px;
  `};
`;

const OutboxMessageWrapper = styled(Link)`
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  padding-left: 20px;
  padding-right: 20px;
  display: block;
  :last-child {
    border-bottom: none;
  }
  ${media.mobile`
    padding-left: 16px;
    padding-right: 16px;
  `};
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default withTranslation()(ProfilePost);
